import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LayoutDashboard, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AuthModal } from './AuthModal';
import { UserAvatar } from './UserAvatar';
import { toast } from 'sonner';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for stored user
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }

    // Listen for storage changes (login/logout from other tabs)
    const handleStorage = () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      } else {
        setUser(null);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Listen for custom login event
  useEffect(() => {
    const handleLogin = (e) => {
      setUser(e.detail);
    };
    window.addEventListener('user-login', handleLogin);
    return () => window.removeEventListener('user-login', handleLogin);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Deconectat cu succes');
    navigate('/');
  };

  const navLinks = [
    { name: 'Circuite Externe', path: '/circuite' },
    { name: 'Circuite România', path: '/circuite-romania' },
    { name: 'City Break', path: '/city-break' },
    { name: 'Concerte', path: '/concerte' },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
              <img 
                src="/logo.webp" 
                alt="VaAi Logo" 
                className="h-10 w-10 rounded-full object-cover"
              />
              <span className="font-heading font-bold text-xl md:text-2xl text-white">
                Va<span className="text-gold">Ai</span>
                <span className="text-white"> - Vacanțe</span>
                <span className="text-gold"> AI</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-white/80 hover:text-gold transition-colors font-medium"
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Auth & Admin */}
            <div className="flex items-center gap-4">
              {/* Dashboard Link - For Super Admin and Operators */}
              {user?.is_super_admin && (
                <Link
                  to="/dashboard"
                  className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 text-gold hover:bg-gold/20 transition-all font-medium"
                  data-testid="dashboard-link"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Link>
              )}
              {user?.role === 'operator' && !user?.is_super_admin && (
                <Link
                  to="/operator"
                  className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 text-gold hover:bg-gold/20 transition-all font-medium"
                  data-testid="operator-dashboard-link"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Link>
              )}

              {user ? (
                <div className="hidden md:flex items-center gap-3">
                  {/* Premium User Avatar with Dropdown */}
                  <UserAvatar user={user} onLogout={handleLogout} />
                </div>
              ) : (
                <Button
                  onClick={() => setShowAuth(true)}
                  variant="outline"
                  className="hidden md:flex items-center gap-2 border-white/20 text-white hover:bg-white/10 hover:border-gold"
                  data-testid="login-btn"
                >
                  <User className="w-4 h-4" />
                  Contul meu
                </Button>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="md:hidden p-2 text-white"
                data-testid="mobile-menu-btn"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="md:hidden py-4 border-t border-white/10 animate-fade-in">
              <div className="flex flex-col gap-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className="text-white/80 hover:text-gold transition-colors py-2 font-medium"
                  >
                    {link.name}
                  </Link>
                ))}
                
                {/* Dashboard for mobile - Super Admin only */}
                {user?.is_super_admin && (
                  <Link
                    to="/dashboard"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2 text-gold py-2 font-medium"
                    data-testid="mobile-dashboard-link"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard Admin
                  </Link>
                )}
                {/* Dashboard for mobile - Operator */}
                {user?.role === 'operator' && !user?.is_super_admin && (
                  <Link
                    to="/operator"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2 text-gold py-2 font-medium"
                    data-testid="mobile-operator-dashboard-link"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard Operator
                  </Link>
                )}
                
                {user ? (
                  <div className="pt-2 border-t border-white/10 mt-2">
                    {/* Mobile User Info */}
                    <div className="flex items-center gap-3 mb-4">
                      <div 
                        className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                        }}
                      >
                        <span className="text-white font-bold text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {user.name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">
                          {user.name}
                        </p>
                        <p className="text-white/50 text-xs">{user.email}</p>
                        {user.is_super_admin && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full">
                            Admin
                          </span>
                        )}
                        {user.role === 'operator' && !user.is_super_admin && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full">
                            Operator
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => {
                        handleLogout();
                        setIsOpen(false);
                      }}
                      className="w-full btn-outline"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Deconectare
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => {
                      setIsOpen(false);
                      setShowAuth(true);
                    }}
                    className="btn-gold mt-2"
                  >
                    Contul meu
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      <AuthModal 
        isOpen={showAuth} 
        onClose={() => setShowAuth(false)} 
        onLogin={(userData) => setUser(userData)}
      />
    </>
  );
};
